import React from "react";

import { keyBy } from "lodash";
import { useTranslation } from "react-i18next";

import Icon from "Atoms/Icons";

import {
  Button,
  ErrorBox,
  LinkButton,
  Price,
  ResponsivePrimaryAction,
  ResponsiveTitleBar,
  useSubmitButton,
} from "../Atoms";
import { createTabCheckoutSession } from "../Cart/requests";
import { useTimeoutSearchParam } from "../Checkout/useTimeoutSearchParam";
import { useLoadedCustomer } from "../Customer/queries";
import { useLoadedCustomerTab } from "../CustomerTab/queries";
import { OrderItem } from "../CustomerTab/schema";
import { getOrderItemTotals } from "../CustomerTab/selectors";
import { useDeleteOrderItemClaim } from "./OrderItemClaims/queries";
import { OrderItemBox, ClaimableOrderItemContent } from "./SelectOrderItems";
import { useLoadedBillSplit } from "./queries";
import { BillSplit, OrderItemClaim } from "./schema";
import { getClaimedOrderItemsIn } from "./selectors";

const ReviewBillSplitSesson = () => {
  const { data: customerTab } = useLoadedCustomerTab();
  const { data: billSplitSession } = useLoadedBillSplit();
  const { data: customer } = useLoadedCustomer();

  const deleteOrderItemClaimMutation =
    useDeleteOrderItemClaim(billSplitSession);

  const deleteOrderItemClaim = (orderItemClaim: OrderItemClaim) => async () => {
    deleteOrderItemClaimMutation.mutate(orderItemClaim);
  };

  const orderItemClaims = keyBy(
    billSplitSession.orderItemClaims,
    "orderItemId"
  );

  const billSplitCustomerSessionId = billSplitSession.customerSessions.find(
    (cs) => cs.customerId === customer.uuid
  )?.uuid;

  const yourOrderItems = getClaimedOrderItemsIn(
    billSplitSession,
    customerTab
  )(customer);

  const yourClaimedOrderItemsTotal = getOrderItemTotals(yourOrderItems);

  const { t } = useTranslation();

  const startBillSplitCheckoutSession = async () => {
    const checkoutUrl = await createTabCheckoutSession(customerTab.uuid, {
      billSplitCustomerSessionId,
    });
    window.location.assign(checkoutUrl);
    await new Promise((r) => setTimeout(r, 3000));
  };

  const hasJustCancelledPayment = useTimeoutSearchParam("payment");
  const isOwner = billSplitSession.ownerId === customer.uuid;
  const [submissionState, trackSubmit] = useSubmitButton();
  return (
    <div className="max-w-screen-sm mx-auto space-y-32">
      <ResponsiveTitleBar>{t("billSplit.review")} </ResponsiveTitleBar>
      {hasJustCancelledPayment && (
        <div className="mx-auto max-w-screen-sm ">
          <ErrorBox>{t("cart.paymentCancelled")}</ErrorBox>
        </div>
      )}
      <div className="space-y-12">
        <h3 className="text-xl font-bold">
          {t("billSplit.yourItems", { count: yourOrderItems.length })}
        </h3>
        <div className="space-y-8">
          {yourOrderItems?.map((oi) => (
            <YourClaimedOrderItem
              billSplit={billSplitSession}
              orderItem={oi}
              deleteOrderItemClaim={deleteOrderItemClaim(
                orderItemClaims[oi.uuid]
              )}
            />
          ))}
        </div>
      </div>
      <div className="space-y-12">
        <ResponsivePrimaryAction>
          {isOwner ? (
            <LinkButton primary to="../ownerReview">
              {t("billSplit.payNow")}
              <Price showCurrency value={yourClaimedOrderItemsTotal} />
            </LinkButton>
          ) : (
            <Button
              primary
              submissionState={submissionState}
              submissionOptions={{
                loadingMessage: t("billSplit.creatingCheckoutSession"),
              }}
              onClick={trackSubmit(startBillSplitCheckoutSession)}
            >
              {t("billSplit.payNow")}
              <Price showCurrency value={yourClaimedOrderItemsTotal} />
            </Button>
          )}
        </ResponsivePrimaryAction>
      </div>
    </div>
  );
};
export default ReviewBillSplitSesson;
export const YourClaimedOrderItem = ({
  orderItem,
  deleteOrderItemClaim,
  billSplit,
  disabled,
}: {
  orderItem: OrderItem;
  billSplit: BillSplit;
  disabled?: boolean;
  deleteOrderItemClaim: () => void;
}) => {
  const { data: customer } = useLoadedCustomer();
  const customerSession = billSplit.customerSessions.find(
    (cs) => cs.customerId === customer.uuid
  );
  return (
    <OrderItemBox disabled={disabled}>
      <ClaimableOrderItemContent orderItem={orderItem} />
      <div>
        {customerSession?.completedAt ? (
          <div>Paid by you</div>
        ) : (
          <div className="text-venue-highlight">
            <button type="button" onClick={deleteOrderItemClaim}>
              <Icon icon="delete" />
            </button>
          </div>
        )}
      </div>
    </OrderItemBox>
  );
};
